import React from 'lib/react';
import { EVGrid } from 'lib/components';
import { PieChart } from '@mui/x-charts/PieChart';
import Typography from 'commonComponents/typography';
import { useTranslation } from 'lib/translation';
import AnalyticsBarChart from 'commonComponents/analyticsBarChart';
import { lineElementClasses, markElementClasses } from '@mui/x-charts/LineChart';
import { useSelector } from 'lib/redux';
import { CLOSED_PERCENTAGE_COLOR, CLOSURE_REASON_COLORS } from '../Analytics.constants';
import LastUpdatedTime from './LastUpdatedTime.component';
import BarChartLegend from './BarChartLegend.component';
import ChartsStyles from './ChartsStyles';

function ClosedPercentageCharts() {
  const classes = ChartsStyles();

  const { t } = useTranslation();

  const reportAnalytics = useSelector(({ analyticsDashboard }) => analyticsDashboard.reportAnalytics);

  const reportsClosureData = Object.entries(reportAnalytics.reduce(
    (acc, report) => {
      const month = new Date(report.orderPlacedDate).getMonth();
      const monthData = acc[month] || {
        Others: 0, 'Poor Images': 0, closedReports: 0, totalReports: 0,
      };
      const totalReports = monthData.totalReports + 1;
      if (report.currentStatus === 'Closed') {
        const reason = report.closureReason;
        const reasonCount = monthData[reason] || 0;
        const closedReports = monthData.closedReports + 1;
        return {
          ...acc,
          [month]: {
            ...monthData, [reason]: reasonCount + 1, totalReports, closedReports,
          },
        };
      }
      return { ...acc, [month]: { ...monthData, totalReports } };
    },
    {},
  )).map(([month, monthData]) => ({ ...monthData, month: Number(month) + 1, closedPercentage: (monthData.closedReports / monthData.totalReports) * 100 }));

  const closedPercentageBarChartSeries = Object.entries(CLOSURE_REASON_COLORS).map(([reason, color]) => ({
    dataKey: reason,
    stack: 'reportsCount',
    color,
    label: reason,
    valueFormatter: (value) => `${value} Reports`,
  }));

  closedPercentageBarChartSeries.push({
    type: 'line',
    dataKey: 'closedPercentage',
    yAxisId: 'rightAxis',
    curve: 'linear',
    color: CLOSED_PERCENTAGE_COLOR,
    label: t('analytics_dashboard__closed_percentage'),
  });

  const closureReasonsPieChartSeriesData = Object.entries(reportsClosureData.reduce((acc, report) => {
    const {
      Others, 'Poor Images': PoorImages, 'No Images': NoImages,
    } = report;
    return {
      ...acc,
      Others: (acc.Others || 0) + (Others || 0),
      'Poor Images': (acc['Poor Images'] || 0) + (PoorImages || 0),
      'No Images': (acc['No Images'] || 0) + (NoImages || 0),
    };
  }, {})).map(([reason, value]) => ({ label: reason, value, color: CLOSURE_REASON_COLORS[reason] }));

  return (
    <>
      <EVGrid className={classes.barChartContainer}>
        <Typography variant="h3-600" fontColor="charcoal-900">{t('analytics_dashboard__closed_percentage')}</Typography>
        <EVGrid className={classes.barChartDiv}>
          <AnalyticsBarChart
            dataset={reportsClosureData}
            series={closedPercentageBarChartSeries}
            xAxis={[{
              scaleType: 'band',
              dataKey: 'month',
              categoryGapRatio: 0.05,
              disableTicks: true,
              valueFormatter: (monthNum) => new Date(2021, monthNum - 1).toLocaleString('default', { month: 'short' }).toUpperCase(),
            },
            ]}
            yAxis={[
              {
                id: 'leftAxis',
                disableTicks: true,
                label: t('analytics_dashboard__volume'),
                valueFormatter: (volume) => Number(volume),
              },
              {
                id: 'rightAxis',
                disableTicks: true,
                label: t('analytics_dashboard__closed_rate_percentage'),
                valueFormatter: (percentage) => `${percentage} %`,
              },
            ]}
            leftAxis="leftAxis"
            rightAxis="rightAxis"
            sx={{
              [`.${lineElementClasses.root}`]: {
                strokeDasharray: '4 4',
              },
              [`.${markElementClasses.root}`]: {
                fill: CLOSED_PERCENTAGE_COLOR,
              },
              '& .MuiChartsAxis-right': {
                stroke: CLOSED_PERCENTAGE_COLOR,
                strokeWidth: 0.5,
              },
            }}
          />
        </EVGrid>
        <EVGrid className={classes.legend}>
          <BarChartLegend
            lengendData={[
              ...Object.entries(CLOSURE_REASON_COLORS).map(([reason, color]) => ({ label: reason, color })),
              { label: t('common__closed'), color: CLOSED_PERCENTAGE_COLOR, type: 'dashedLine' },
            ]}
          />
          <LastUpdatedTime />
        </EVGrid>
      </EVGrid>
      <EVGrid className={classes.pieChartsContainer}>
        <Typography variant="body-400" fontColor="charcoal-900">{t('analytics_dashboard__closure_reason')}</Typography>
        <PieChart
          sx={{
            '& *': {
              fontFamily: 'Noto Sans',
            },
          }}
          series={[{ data: closureReasonsPieChartSeriesData }]}
          width={400}
          height={200}
        />
      </EVGrid>
    </>
  );
}

export default ClosedPercentageCharts;
